import { apiClient, ApiError } from '../../utils/api-client';

export interface TranslationResult {
  success: boolean;
  translation?: string;
  errorMessage?: string;
  errorType?: 'payment_required' | 'rate_limit' | 'forbidden' | 'server_error' | 'timeout' | 'untranslatable';
}

const UNTRANSLATABLE_REGEX = /^[\d\s\p{P}]+$/u;
const REQUEST_TIMEOUT_MS = 30000;

class TranslationService {
  private pendingRequests = new Map<string, Promise<TranslationResult>>();
  private cache = new Map<string, TranslationResult>();
  private balanceRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  private scheduleBalanceRefresh(): void {
    if (this.balanceRefreshTimer) clearTimeout(this.balanceRefreshTimer);
    this.balanceRefreshTimer = setTimeout(() => {
      window.dispatchEvent(new Event('balance-changed'));
      this.balanceRefreshTimer = null;
    }, 2000);
  }

  private generateKey(text: string, source: string, target: string, context?: string): string {
    const contextKey = context ? `|ctx:${this.hashCode(context)}` : '';
    return `${source}\u2192${target}|${text}${contextKey}`;
  }

  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash |= 0;
    }
    return hash;
  }

  isUntranslatable(text: string): boolean {
    const trimmed = text.trim();
    if (!trimmed) return true;
    return UNTRANSLATABLE_REGEX.test(trimmed);
  }

  async translate(params: {
    text: string;
    sourceLanguage: string;
    targetLanguage: string;
    context?: string;
  }): Promise<TranslationResult> {
    const { text, sourceLanguage, targetLanguage, context } = params;

    // Skip untranslatable content
    if (this.isUntranslatable(text)) {
      return { success: true, translation: text, errorType: 'untranslatable' };
    }

    const key = this.generateKey(text, sourceLanguage, targetLanguage, context);

    // Return cached result if available (avoids repeated API costs)
    const cached = this.cache.get(key);
    if (cached) return cached;

    // Deduplication: return existing pending request
    const pending = this.pendingRequests.get(key);
    if (pending) return pending;

    const promise = this.executeTranslation(text, sourceLanguage, targetLanguage, context);
    this.pendingRequests.set(key, promise);

    try {
      const result = await promise;
      // Cache successful translations (don't cache errors — they may be transient)
      if (result.success) {
        this.cache.set(key, result);
      }
      return result;
    } finally {
      this.pendingRequests.delete(key);
    }
  }

  private async executeTranslation(
    text: string,
    sourceLanguage: string,
    targetLanguage: string,
    context?: string,
  ): Promise<TranslationResult> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await apiClient.post<{ result: { translatedText: string } }>(
        '/reader/articles/translate',
        {
          text,
          sourceLanguage,
          targetLanguage,
          ...(context && { context }),
        },
      );

      const translation = response.result.translatedText;
      if (!translation) {
        return { success: false, errorMessage: 'Empty translation', errorType: 'server_error' };
      }

      this.scheduleBalanceRefresh();
      return { success: true, translation };
    } catch (err) {
      if (err instanceof ApiError) {
        switch (err.status) {
          case 402:
            return { success: false, errorMessage: 'Insufficient funds', errorType: 'payment_required' };
          case 429:
            return { success: false, errorMessage: 'Rate limit exceeded', errorType: 'rate_limit' };
          case 403:
            return { success: false, errorMessage: 'Access denied', errorType: 'forbidden' };
          default:
            return { success: false, errorMessage: `Server error (${err.status})`, errorType: 'server_error' };
        }
      }

      if (err instanceof DOMException && err.name === 'AbortError') {
        return { success: false, errorMessage: 'Translation timeout', errorType: 'timeout' };
      }

      return { success: false, errorMessage: 'Translation failed', errorType: 'server_error' };
    } finally {
      clearTimeout(timeout);
    }
  }

  onLanguageChange(): void {
    this.pendingRequests.clear();
    this.cache.clear();
  }

  get pendingCount(): number {
    return this.pendingRequests.size;
  }
}

// Singleton instance shared across all chunks
export const translationService = new TranslationService();
